import { springUrl } from "app/shared/constants/Url";
import {
  DASHBOARD_TAB,
  SCHEDULED_TAB,
  INPROGRESS_TAB,
  APPROVAL_TAB,
  COMPLETED_TAB,
  CLOSED_TAB,
  DECLINED_TAB,
  RECOMMENDATION_TAB,
  TEMPALTES_TAB,
  CALENDAR_TAB,
  ALL_COMPLETED_TAB,
} from "app/shared/utility/helperStrings";

// Map tab names to original constants
export const CHECKLIST_TABS = {
  dashboard: DASHBOARD_TAB,
  calendar: CALENDAR_TAB,
  recommendation: RECOMMENDATION_TAB,
  scheduled: SCHEDULED_TAB,
  inprogress: INPROGRESS_TAB,
  approval: APPROVAL_TAB,
  completed: COMPLETED_TAB,
  closed: CLOSED_TAB,
  declined: DECLINED_TAB,
  templates: TEMPALTES_TAB,
  allCompleted: ALL_COMPLETED_TAB,
};

// Checklist specific constants
export const CHECKLIST_FORM_TYPE = "5";
export const CHECKLIST_MODULE_NAME = "WORK_PERMIT";

// ✅ Define all route patterns with their types
export const CHECKLIST_ROUTES = {
  // Base
  BASE: "/work-permit",
  DASHBOARD: "/work-permit/dashboard",

  // Tab routes (these will render inside Tab.Container)
  TAB: "/work-permit/:tab",
  VIEW_FORM:
    "/work-permit/:tab/view-form/:form_id/:service_id/:project_id/:wpu_id/:timeline_id/:survey_id/:elearning_id/:user_id/:form_reporting_assign_id",
  USER_FORM_VIEW:
    "/work-permit/:tab/user-form-view/:form_id/:service_id/:project_id/:wpu_id/:timeline_id/:elearning_id/:form_reporting_assign_id",
  PUBLISHED_VIEW: "/work-permit/:tab/published-view/:form_id",

  // ✅ Standalone routes (no tabs)
  //LIST_TEMPLATE: "/work-permit/templates",
  CREATE_TEMPLATE: "/work-permit/create-template",
  EDIT_TEMPLATE: "/work-permit/edit-template/:templateId",
  VIEW_TEMPLATE: "/work-permit/view-template/:templateId",
  ASSIGN_TEMPLATE: "/work-permit/assign-template/:template_id/:module",
  TEMPLATE_SETTINGS: "/work-permit/template-settings",
  IMPORT_TEMPLATE: "/work-permit/import-template",

  // Global routes
  UNLOCK_FORM: "/unlock-form-view/:service_id/:project_id/:form_id/:status",
};

// ✅ Define route configuration - which routes are standalone vs tab routes
export const ROUTE_CONFIG = {
  // Tab routes - render with Tab.Container and SideMenu
  tabRoutes: [
    "/work-permit/dashboard",
    "/work-permit/:tab",
    "/work-permit/:tab/view-form/",
    "/work-permit/:tab/user-form-view/",
    "/work-permit/:tab/published-view/",
    "/work-permit/calendar",
  ],

  // Standalone routes - render WITHOUT Tab.Container and SideMenu
  standaloneRoutes: [
    "/work-permit/create-template",
    "/work-permit/edit-template/",
    "/work-permit/view-template/",
    "/work-permit/assign-template/",
    "/work-permit/template-settings",
    "/work-permit/import-template",
  ],
  // Detail screen patterns (hide side menu but keep tabs)
  detailScreenPatterns: ["/view-form/", "/user-form-view/", "/published-view/"],
};

// ✅ Helper: Check if current route is a standalone route
export const isStandaloneRoute = (pathname) => {
  return ROUTE_CONFIG.standaloneRoutes.some((pattern) => {
    // Handle patterns with parameters (like /edit-template/:id)
    const patternWithoutParams = pattern.replace(/\/:[^/]+/g, "");
    return pathname.includes(patternWithoutParams);
  });
};

// ✅ Helper: Check if current route is a detail screen (tab route but hide side menu)
export const isDetailScreenRoute = (pathname) => {
  return ROUTE_CONFIG.detailScreenPatterns.some((pattern) =>
    pathname.includes(pattern),
  );
};

// ✅ Helper: Check if route should show tabs
export const shouldShowTabs = (pathname) => {
  return !isStandaloneRoute(pathname);
};

// ✅ Helper: Check if route should show side menu
export const shouldShowSideMenu = (pathname) => {
  return shouldShowTabs(pathname) && !isDetailScreenRoute(pathname);
};

// ✅ Helper: Extract tab from pathname (only for tab routes)
export const getTabFromPath = (pathname) => {
  if (isStandaloneRoute(pathname)) {
    return null; // No tab for standalone routes
  }

  const pathWithoutBase = pathname.replace("/work-permit/", "");
  const tabSegment = pathWithoutBase.split("/")[0];

  const tabEntry = Object.entries(CHECKLIST_TABS).find(
    ([_, value]) => value === tabSegment,
  );

  return tabEntry ? tabEntry[0] : "dashboard";
};

export const TEMPLATE_API_ENDPOINT = {
  createTemplate: `${springUrl}/template/createTemplate`,
  templateDetails: `${springUrl}/template/details`,
  addSection: `${springUrl}/template/addSection`,
  delete: `${springUrl}/template/delete`,
  templateList: `${springUrl}/template/list`,
  addQuestion: `${springUrl}/template/addQuestion`,
  uploadFile: `${springUrl}/template/uploadFile`,
  publish: `${springUrl}/template/publish`,
  grantAccess: `${springUrl}/template/grantAccess`,
  accessList: `${springUrl}/template/accessList`,
  revokeAccess: `${springUrl}/template/revokeAccess`,
  userProjectAccessList: `${springUrl}/template/userProjectAccessList`,
  projectAssign: `${springUrl}/template/assignToProject`,
  removeProject: `${springUrl}/template/removeFromProject`,
};

// Work Permit dashboard/graph API endpoints
export const WORK_PERMIT_API_ENDPOINT = {
  // GET - query params: user_id, auth_key, form_type, start_date, end_date, project_id
  dbAvgPerCompliance: `${springUrl}/workpermit/listgraph`,
  scheduleList: `${springUrl}/workpermit/scheduleList`,
  // GET - query params: user_id, auth_key, sheduleId
  details: `${springUrl}/workpermit/details`,
  createSchedule: `${springUrl}/workpermit/createUpdateSchedule`,
  // DELETE - query params: user_id, auth_key, sheduleId
  delete: `${springUrl}/workpermit/delete`,
  // DELETE - query params: user_id, auth_key, sheduleId
  restore: `${springUrl}/workpermit/restore`,
  // POST - query params: user_id, auth_key, schedule_id, closing_remark, closing_reason, closing_date
  close: `${springUrl}/workpermit/close`,
  // POST - query params: user_id, auth_key, schedule_id, extend_remark, extend_reason, end_date
  extend: `${springUrl}/workpermit/extend`,
  // GET - query params: user_id, auth_key, project_id, start_date, end_date
  counts: `${springUrl}/workpermit/counts`,
  // POST - query params: user_id, auth_key
  userResponse: `${springUrl}/workpermit/userResponse`,
  // POST - query params: user_id, auth_key
  uploadResponseFile: `${springUrl}/workpermit/uploadResponseFile`,
  // POST - body: schedule_id, action, remarks
  approve: `${springUrl}/workpermit/approve`,
  // POST - body: schedule_id, decline_reason, decline_remark
  decline: `${springUrl}/workpermit/decline`,
  workPermitData: `${springUrl}/workpermit/scheduleDetail`,
};
